/**
 * Política de limpieza de almacenamiento local ante actualización de versión.
 * @module utils/versionStoragePolicy
 */

export const APP_VERSION_SNAPSHOT_KEY = 'app_version_snapshot';
export const PENDING_APP_VERSION_KEY = 'pending_app_version';

/** Sucursales conocidas obsoletas (desactivadas en tenant). */
export const OBSOLETE_SUCURSAL_IDS = new Set([
  'AhXpWMMJcarEP8guQVCS'
]);

/** Claves exactas que nunca se eliminan en autorefresh. */
export const PROTECTED_EXACT_KEYS = new Set([
  APP_VERSION_SNAPSHOT_KEY,
  PENDING_APP_VERSION_KEY,
  'orgId',
  'companyId',
  'nexo_device_id',
  'nexo_session_id',
  'nexo_session_started_at_ms',
  'nexo_access_mode',
  'mobile_pos_offline_sales_v1',
  'mobile_pos_session_v1',
  'margenes_listas',
  'ultimo_metodo_pago',
  'reparto_sesion_last',
  'preventa_zonas',
  'preventa_pedidos_local',
  'cajero_price_debug',
  'cajero-hotkey-hint-dismissed',
  'token',
  'user'
]);

/** Prefijos protegidos (venta activa, offline, sesión). */
export const PROTECTED_PREFIXES = [
  'mobile_pos_session_v1',
  'mobile_pos_offline_sales_v1',
  'nexo_session_id_',
  'nexo_session_started_at_ms_'
];

/** Prefijos limpiables en actualización segura. */
export const CLEANABLE_PREFIXES = [
  'mobile_pos_catalog_v1:',
  'nexopos_gtin_cache_v1:'
];

/** Claves exactas limpiables (cachés de módulos / UI local). */
export const CLEANABLE_EXACT_KEYS = new Set([
  'jardinTareas',
  'muroInnovacion',
  'sucursalSeleccionada'
]);

export function isProtectedStorageKey(key) {
  if (!key || typeof key !== 'string') return true;
  if (PROTECTED_EXACT_KEYS.has(key)) return true;
  return PROTECTED_PREFIXES.some((prefix) => key.startsWith(prefix));
}

export function isCleanableStorageKey(key) {
  if (!key || typeof key !== 'string') return false;
  if (isProtectedStorageKey(key)) return false;
  if (CLEANABLE_EXACT_KEYS.has(key)) return true;
  return CLEANABLE_PREFIXES.some((prefix) => key.startsWith(prefix));
}

function safeRemoveLocalStorageKey(key) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/**
 * Limpia únicamente claves obsoletas permitidas por la política.
 * @returns {{ removed: string[], skipped: string[] }}
 */
export function purgeCleanableLocalStorage() {
  const removed = [];
  const skipped = [];

  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      keys.push(localStorage.key(i));
    }

    keys.forEach((key) => {
      if (!key) return;
      if (isCleanableStorageKey(key)) {
        if (safeRemoveLocalStorageKey(key)) removed.push(key);
      } else if (!isProtectedStorageKey(key)) {
        skipped.push(key);
      }
    });
  } catch {
    // storage no disponible
  }

  return { removed, skipped };
}
